import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  CreditCard,
  BookOpen,
  Radio,
  BarChart3,
  Receipt,
  Settings,
  ChevronLeft,
  ChevronRight,
  Target,
  Activity,
  Bot,
  GitBranch,
  ShieldCheck,
} from "lucide-react";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useAuthStore, canWrite, canManageTeam } from "@/stores/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { initials } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  domains?: string[];  // if set, only show for these domains
  requireWrite?: boolean;  // only show if user can write
  requireTeamManage?: boolean;  // only show if user can manage team
}

const navItems: NavItem[] = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Agent Chat", path: "/agent", icon: Bot },
  { label: "Workflows", path: "/workflows", icon: GitBranch },
  { label: "Approvals", path: "/approvals", icon: ShieldCheck, requireWrite: true },
  { label: "Conversations", path: "/conversations", icon: MessageSquare },
  { label: "Customers", path: "/customers", icon: Users },
  { label: "Live Activity", path: "/live", icon: Activity },
  { label: "Payments", path: "/payments", icon: CreditCard },
  { label: "Knowledge Base", path: "/knowledge-base", icon: BookOpen },
  { label: "Channels", path: "/channels", icon: Radio, requireWrite: true },
  { label: "Leads", path: "/leads", icon: Target },
  { label: "Analytics", path: "/analytics", icon: BarChart3 },
  { label: "Billing", path: "/billing", icon: Receipt, requireTeamManage: true },
  { label: "Settings", path: "/settings", icon: Settings, requireWrite: true },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  const tenantDomain = tenant?.domain;
  const userRole = user?.role;

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (item.domains && (!tenantDomain || !item.domains.includes(tenantDomain))) return false;
      if (item.requireWrite && !canWrite(userRole)) return false;
      if (item.requireTeamManage && !canManageTeam(userRole)) return false;
      return true;
    });
  }, [tenantDomain, userRole]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar-collapsed", String(!prev));
      return !prev;
    });
  };

  return (
    <aside
      className={cn(
        "flex flex-col h-screen bg-sidebar text-sidebar-foreground transition-all duration-200 border-r border-sidebar-accent",
        collapsed ? "w-16" : "w-60",
      )}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-sidebar-accent">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground font-bold text-sm">
          C
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-sm font-semibold truncate">Concierge</p>
            {tenant && (
              <p className="text-xs text-sidebar-muted truncate">{tenant.name}</p>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-1 px-2">
        {visibleNavItems.map(({ label, path, icon: Icon }) => {
          const isActive = location.pathname.startsWith(path);
          return (
            <NavLink
              key={path}
              to={path}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-foreground"
                  : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
              )}
              title={collapsed ? label : undefined}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-sidebar-accent p-3">
        <UserCard collapsed={collapsed} />
      </div>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={toggleCollapsed}
        className="flex items-center justify-center h-10 border-t border-sidebar-accent text-sidebar-muted hover:text-sidebar-foreground transition-colors"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}

function UserCard({ collapsed }: { collapsed: boolean }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-xs bg-primary/20 text-primary">
          {initials(user.name)}
        </AvatarFallback>
      </Avatar>
      {!collapsed && (
        <div className="overflow-hidden">
          <p className="text-xs font-medium truncate">{user.name}</p>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {user.role}
          </Badge>
        </div>
      )}
    </div>
  );
}
